import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { LoadingSpinner } from '@/components/features/LoadingSpinner'
import { LineBasedInvoicePageContent } from '@/components/features/invoicing/LineBasedInvoicePageContent'
import { getDirectSaleOrder } from '@/lib/api/direct-sale-order'
import { getProjectInvoices } from '@/lib/api/projects'
import type { CreateInvoiceLineDto } from '@/types'

export default function DirectInvoicePage() {
  const { id: projectId } = useParams<{ id: string }>()

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [remainingAmount, setRemainingAmount] = useState(0)
  const [initialLines, setInitialLines] = useState<CreateInvoiceLineDto[]>([])
  const [defaultVatPercent, setDefaultVatPercent] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (!projectId) return
    const load = async () => {
      try {
        const [order, invoices] = await Promise.all([
          getDirectSaleOrder(projectId),
          getProjectInvoices(projectId),
        ])

        const lines: CreateInvoiceLineDto[] = order.lines.map((l) => ({
          kind: l.kind as CreateInvoiceLineDto['kind'],
          categoryLabel: l.categoryLabel ?? undefined,
          title: l.title,
          description: l.description ?? undefined,
          quantity: l.quantity ?? undefined,
          unitPriceWithoutTax: l.unitPriceWithoutTax ?? undefined,
          discountPercent: l.discountPercent ?? undefined,
          vatPercent: l.vatPercent ?? undefined,
        }))

        setInitialLines(lines)
        setRemainingAmount(invoices.project.totalRemaining)
        setDefaultVatPercent(order.defaultVatPercent ?? undefined)
      } catch {
        setError('Erreur lors du chargement des données de la commande')
      } finally {
        setIsLoading(false)
      }
    }
    void load()
  }, [projectId])

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <LineBasedInvoicePageContent
        projectId={projectId ?? ''}
        title="Facture directe"
        description="Lignes pré-remplies depuis la commande — modifiables"
        remainingAmount={remainingAmount}
        initialLines={initialLines}
        invoiceType="direct_sale"
        error={error}
        defaultVatPercent={defaultVatPercent}
      />
    </DashboardLayout>
  )
}
